import React, {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useValidateAttachmentMutation } from '../../../../features/ChatModule';
import { ALERT_BANNER_DEFAULT } from '../../../constants';

interface FormContextType {
  dependencyMapRef: React.MutableRefObject<Map<string, string[]>>;
  alertBanner: typeof ALERT_BANNER_DEFAULT;
  setAlertBanner: React.Dispatch<
    React.SetStateAction<typeof ALERT_BANNER_DEFAULT>
  >;
  workflowType?: string;
}

const FormContext = createContext<FormContextType | null>(null);

/**
 * FormProvider
 */
function FormProvider({
  children,
  workflowType,
}: {
  children: React.ReactNode;
  workflowType?: string;
}) {
  const dependencyMapRef = useRef<Map<string, string[]>>(new Map());

  const [alertBanner, setAlertBanner] = useState(ALERT_BANNER_DEFAULT);
  const value = useMemo(
    () => ({ dependencyMapRef, alertBanner, setAlertBanner, workflowType }),
    [alertBanner, workflowType]
  );

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
}

export const useFormContext = () => {
  const context = useContext(FormContext);

  if (!context) {
    throw new Error('useFormContext must be used within a FormProvider');
  }

  return context;
};

export interface FailedFile {
  name: string;
  error: string;
}

export interface FileValidationResult {
  failedFiles: FailedFile[];
  successMessage: string;
}

export const useFileValidation = () => {
  const [validateAttachment, { isLoading }] = useValidateAttachmentMutation();

  const validateFiles = async (
    files: File[]
  ): Promise<FileValidationResult> => {
    try {
      const apiCalls = files.map((file) => {
        const formData = new FormData();

        formData.append('files', file);

        return validateAttachment(formData).unwrap();
      });
      const results = await Promise.allSettled(apiCalls);

      let successMessage = '';

      const failedFiles = results.reduce<FailedFile[]>((acc, result, index) => {
        if (result.status === 'rejected') {
          const reason = result.reason as Record<string, unknown>;
          const axiosErr = reason.data as
            | { response?: { data?: { error?: string } }; message?: string }
            | undefined;
          const errorMessage =
            axiosErr?.response?.data?.error ??
            axiosErr?.message ??
            'Virus scan has failed';

          acc.push({ name: files[index].name, error: errorMessage });
        } else {
          const response = result.value as { message?: string } | undefined;

          if (response?.message) {
            successMessage = response.message;
          }
        }

        return acc;
      }, []);

      return { failedFiles, successMessage };
    } catch (error) {
      throw Error('Failed file upload');
    }
  };

  return { validateFiles, isUploading: isLoading };
};

FormProvider.defaultProps = {
  workflowType: undefined,
};

export default FormProvider;
